import { useCallback } from "react"
import type React from "react"

interface DeleteSimulationOutputMutation {
  mutateAsync: (input: { simulation_output_id: string }) => Promise<unknown>
}

interface RunResultsBulkDeleteLogger {
  error: (message: string, error: unknown) => void
}

interface UseRunResultsBulkDeleteParams {
  deleteSimulationOutput: DeleteSimulationOutputMutation
  itemsToDelete: string[]
  log: RunResultsBulkDeleteLogger
  onCloseDeleteModal: () => void
  onOpenDeleteModal: () => void
  selectedSimulationOutputIds: string[]
  setItemsToDelete: React.Dispatch<React.SetStateAction<string[]>>
  setSelectedKeys: (keys: Set<string>) => void
}

export function useRunResultsBulkDelete({
  deleteSimulationOutput,
  itemsToDelete,
  log,
  onCloseDeleteModal,
  onOpenDeleteModal,
  selectedSimulationOutputIds,
  setItemsToDelete,
  setSelectedKeys,
}: UseRunResultsBulkDeleteParams) {
  const handleBulkDelete = useCallback(() => {
    if (selectedSimulationOutputIds.length === 0) {
      return
    }

    setItemsToDelete(selectedSimulationOutputIds)
    onOpenDeleteModal()
  }, [onOpenDeleteModal, selectedSimulationOutputIds, setItemsToDelete])

  const confirmBulkDelete = useCallback(async () => {
    try {
      for (const simulationOutputId of itemsToDelete) {
        if (simulationOutputId) {
          await deleteSimulationOutput.mutateAsync({
            simulation_output_id: simulationOutputId,
          })
        }
      }

      setSelectedKeys(new Set([]))
      setItemsToDelete([])
      onCloseDeleteModal()
    } catch (error) {
      log.error("Error deleting simulation outputs", error)
    }
  }, [
    itemsToDelete,
    deleteSimulationOutput,
    onCloseDeleteModal,
    setItemsToDelete,
    setSelectedKeys,
    log,
  ])

  return {
    confirmBulkDelete,
    handleBulkDelete,
  }
}
